import { CalendarDays, Heart } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import ImageUpload from '@/components/ImageUpload'
import PaymentModeCards from './PaymentModeCards'
import FundraisingProgressFields from './FundraisingProgressFields'
import { ClassicIllustration, GalleryIllustration, TemplateOption } from './TemplateOption'
import {
  DRESS_CODE_LABELS,
  EVENT_BACKGROUNDS,
  type EventFormEventData,
  type FieldError,
} from './shared'

type SectionProps = {
  value: EventFormEventData
  onChange: (next: EventFormEventData) => void
  fieldError: FieldError
}

export function PageTypeSection({ value, onChange }: Pick<SectionProps, 'value' | 'onChange'>) {
  const isFundraiser = value.page_kind === 'fundraiser'

  return (
    <Card>
      <CardHeader>
        <CardTitle>Campaign type</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-2">
          <TemplateOption
            active={!isFundraiser}
            onSelect={() => onChange({ ...value, page_kind: 'event' })}
            title="Event page"
            description="Date-based gala page with tickets, venue, and event details."
            illustration={<CalendarDays className="size-10 text-primary/60" />}
          />
          <TemplateOption
            active={isFundraiser}
            onSelect={() => onChange({ ...value, page_kind: 'fundraiser' })}
            title="Fundraising page"
            description="Donation-first page without date, venue, tickets, or maps."
            illustration={<Heart className="size-10 text-primary/60" fill="currentColor" />}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export function GeneralInfoSection({ value, onChange, fieldError }: SectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>General info</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            required
            value={value.title}
            onChange={(event) => onChange({ ...value, title: event.target.value })}
          />
          {fieldError('title') && <p className="text-base text-destructive">{fieldError('title')}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            rows={5}
            value={value.description}
            onChange={(event) => onChange({ ...value, description: event.target.value })}
          />
        </div>
        <div className="flex items-start justify-between gap-4 rounded-xl border border-black/10 bg-muted/30 p-4">
          <div className="space-y-1">
            <Label htmlFor="visibility" className="text-foreground">Private event</Label>
            <p className="text-xs text-muted-foreground">
              Visible only to signed-in Gala Season members. Public events can be discovered and viewed by anyone.
            </p>
          </div>
          <Switch
            id="visibility"
            checked={value.visibility === 'private'}
            onCheckedChange={(checked) => onChange({ ...value, visibility: checked ? 'private' : 'public' })}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export function CoverImageSection({
  currentUrl,
  onPreviewChange,
  value,
  onChange,
}: Pick<SectionProps, 'value' | 'onChange'> & {
  currentUrl?: string | null
  onPreviewChange: (url: string | null) => void
}) {
  const imageGuidance = value.template_style === 'gallery'
    ? <>Recommended: 1080&times;1350 (4:5 portrait), centered subject, under 10&nbsp;MB. Edges are cropped to fit.</>
    : <>Recommended: 1920&times;1080 (16:9 landscape), centered subject, under 10&nbsp;MB. Edges are cropped to fit.</>

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cover image</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <ImageUpload
          currentUrl={currentUrl}
          onUpload={(signedId) => {
            onChange({
              ...value,
              cover_image: signedId || null,
              remove_cover_image: !signedId,
            })
          }}
          onPreviewChange={onPreviewChange}
        />
        <p className="text-xs text-muted-foreground">
          {imageGuidance}
        </p>
      </CardContent>
    </Card>
  )
}

export function DateVenueSection({ value, onChange, fieldError }: SectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Date & location</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="date">Date *</Label>
            <Input
              id="date"
              type="date"
              required
              value={value.date}
              onChange={(event) => onChange({ ...value, date: event.target.value })}
            />
            {fieldError('date') && <p className="text-base text-destructive">{fieldError('date')}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="start_time">Start time</Label>
            <Input
              id="start_time"
              type="time"
              value={value.start_time}
              onChange={(event) => onChange({ ...value, start_time: event.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="end_time">End time</Label>
            <Input
              id="end_time"
              type="time"
              value={value.end_time}
              onChange={(event) => onChange({ ...value, end_time: event.target.value })}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="venue_name">Venue name</Label>
          <Input
            id="venue_name"
            value={value.venue_name}
            onChange={(event) => onChange({ ...value, venue_name: event.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="street_address">Street address</Label>
          <Input
            id="street_address"
            value={value.street_address}
            onChange={(event) => onChange({ ...value, street_address: event.target.value })}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Input value={value.city} placeholder="City" onChange={(event) => onChange({ ...value, city: event.target.value })} />
          <Input value={value.state} placeholder="State" onChange={(event) => onChange({ ...value, state: event.target.value })} />
          <Input value={value.zip} placeholder="Zip" onChange={(event) => onChange({ ...value, zip: event.target.value })} />
        </div>
      </CardContent>
    </Card>
  )
}

export function EventSettingsSection({
  value,
  onChange,
  fieldError,
  dressCodes,
  causes,
  isAdmin,
}: SectionProps & {
  dressCodes: string[]
  causes: string[]
  isAdmin: boolean
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Event details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="dress_code">Dress code</Label>
            <Select
              value={value.dress_code}
              onValueChange={(next) => onChange({ ...value, dress_code: next === 'none' ? '' : next })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Not specified" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="none">Not specified</SelectItem>
                  {[...dressCodes].sort((a, b) => (DRESS_CODE_LABELS[a] || a).localeCompare(DRESS_CODE_LABELS[b] || b)).map((code) => (
                    <SelectItem key={code} value={code}>{DRESS_CODE_LABELS[code] || code}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          {isAdmin && <StatusSelect value={value} onChange={onChange} />}
        </div>

        {isAdmin && (
          <AdminCauses value={value} onChange={onChange} causes={causes} fieldError={fieldError} />
        )}

        {isAdmin && (
          <div className="space-y-2">
            <Label htmlFor="starting_ticket_price">Starting ticket price</Label>
            <Input
              id="starting_ticket_price"
              type="number"
              step="0.01"
              min="0"
              value={value.starting_ticket_price}
              onChange={(event) => onChange({ ...value, starting_ticket_price: event.target.value })}
            />
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="purchase_button_text">Ticket button text</Label>
          <Input
            id="purchase_button_text"
            maxLength={40}
            placeholder="Get Tickets"
            value={value.purchase_button_text}
            onChange={(event) => onChange({ ...value, purchase_button_text: event.target.value })}
          />
          <p className="text-xs text-muted-foreground">Leave blank to use the default label.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="hashtags_input">Hashtags</Label>
          <Input
            id="hashtags_input"
            placeholder="charity, gala, fundraiser"
            value={value.hashtags_input}
            onChange={(event) => onChange({ ...value, hashtags_input: event.target.value })}
          />
        </div>
        <div className="flex flex-wrap gap-6 pt-2">
          <SwitchLine id="countdown_timer" label="Countdown timer" checked={value.countdown_timer} onChange={(checked) => onChange({ ...value, countdown_timer: checked })} />
          <SwitchLine id="auction_items" label="Accepting auction items" checked={value.auction_items} onChange={(checked) => onChange({ ...value, auction_items: checked })} />
          <SwitchLine id="gift_items" label="Seeking gift bag donations" checked={value.gift_items} onChange={(checked) => onChange({ ...value, gift_items: checked })} />
        </div>
      </CardContent>
    </Card>
  )
}

export function FundraisingSection({
  value,
  onChange,
  fieldError,
  stripePaymentReady,
  isAdmin,
  isPro,
}: SectionProps & {
  stripePaymentReady: boolean
  isAdmin: boolean
  isPro: boolean
}) {
  const isExternal = value.payment_mode === 'external'
  const showNativeDonations = !isAdmin && !isExternal

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ticketing &amp; payments</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isAdmin && <StatusSelect value={value} onChange={onChange} />}
        <PaymentModeCards
          kind="fundraiser"
          mode={value.payment_mode}
          onModeChange={(mode) => onChange({ ...value, payment_mode: mode })}
          externalUrl={value.ticket_link}
          onExternalUrlChange={(url) => onChange({ ...value, ticket_link: url })}
          urlError={fieldError('ticket_link')}
        />
        {showNativeDonations && (
          !stripePaymentReady ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              Connect Stripe to accept donations through Gala Season.{' '}
              <a href="/dashboard/settings/payments" className="font-medium underline">Set up payments</a>.
            </div>
          ) : (
            <SwitchLine
              id="donations_enabled"
              label="Enable native donations"
              checked={value.donations_enabled}
              onChange={(checked) => onChange({ ...value, donations_enabled: checked })}
            />
          )
        )}
        {fieldError('donations_enabled') && <p className="text-base text-destructive">{fieldError('donations_enabled')}</p>}
        {showNativeDonations && stripePaymentReady && (
          <FundraisingProgressFields value={value} onChange={onChange} fieldError={fieldError} />
        )}
        {isPro ? (
          <div className="space-y-2">
            <Label htmlFor="donation_url">Additional donation link</Label>
            <Input
              id="donation_url"
              type="url"
              value={value.donation_url}
              onChange={(event) => onChange({ ...value, donation_url: event.target.value })}
              placeholder="https://donate.example.com"
            />
            <p className="text-xs text-muted-foreground">Optional secondary &ldquo;Donate&rdquo; link shown alongside your page.</p>
          </div>
        ) : (
          <div className="flex items-center justify-between rounded-md border border-black/10 bg-muted/40 p-4">
            <p className="text-base text-muted-foreground">External donation links are a Pro feature.</p>
            <Button asChild variant="outline" size="sm" className="ml-4 shrink-0">
              <a href="/dashboard/billing">Upgrade</a>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function AppearanceSection({ value, onChange, fieldError }: SectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Appearance</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <Label>Template</Label>
          <div className="grid gap-3 sm:grid-cols-2">
            <TemplateOption
              active={value.template_style === 'classic'}
              onSelect={() => onChange({ ...value, template_style: 'classic' })}
              title="Classic"
              description="Full-width hero image with a sticky details sidebar."
              illustration={<ClassicIllustration />}
            />
            <TemplateOption
              active={value.template_style === 'gallery'}
              onSelect={() => onChange({ ...value, template_style: 'gallery' })}
              title="Gallery"
              description="Banner on the left with title and details inline."
              illustration={<GalleryIllustration />}
            />
          </div>
        </div>
        <div className="space-y-3">
          <Label>Background color</Label>
          <div className="flex flex-wrap gap-4">
            {(Object.keys(EVENT_BACKGROUNDS) as Array<keyof typeof EVENT_BACKGROUNDS>).map((key) => {
              const active = (value.background_color || 'default') === key
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onChange({ ...value, background_color: key })}
                  className="flex flex-col items-center gap-1.5"
                >
                  <span
                    className={`inline-block size-10 rounded-full border border-black/10 transition ${active ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                    style={{ backgroundColor: EVENT_BACKGROUNDS[key].color }}
                  />
                  <span className={`text-xs ${active ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    {EVENT_BACKGROUNDS[key].label}
                  </span>
                </button>
              )
            })}
          </div>
          {fieldError('background_color') && <p className="text-base text-destructive">{fieldError('background_color')}</p>}
        </div>
      </CardContent>
    </Card>
  )
}

function StatusSelect({ value, onChange }: Pick<SectionProps, 'value' | 'onChange'>) {
  return (
    <div className="space-y-2">
      <Label htmlFor="status">Status</Label>
      <Select value={value.status} onValueChange={(next) => onChange({ ...value, status: next as 'draft' | 'published' })}>
        <SelectTrigger id="status">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
}

function SwitchLine({ id, label, checked, onChange }: {
  id: string
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
      <Label htmlFor={id}>{label}</Label>
    </div>
  )
}

function AdminCauses({ value, onChange, causes, fieldError }: Pick<SectionProps, 'value' | 'onChange' | 'fieldError'> & { causes: string[] }) {
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="primary_cause">Primary cause</Label>
        <Select
          value={value.primary_cause}
          onValueChange={(selected) => {
            const next = selected === 'none' ? '' : selected
            onChange({
              ...value,
              primary_cause: next,
              causes: next ? value.causes.filter((cause) => cause !== next) : value.causes,
            })
          }}
        >
          <SelectTrigger id="primary_cause">
            <SelectValue placeholder="Select a cause" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="none">None</SelectItem>
              {[...causes].sort().map((cause) => (
                <SelectItem key={cause} value={cause}>{cause}</SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        {fieldError('primary_cause') && <p className="text-base text-destructive">{fieldError('primary_cause')}</p>}
      </div>
      <div className="space-y-2">
        <Label>Additional causes <Badge variant="outline">up to 7</Badge></Label>
        <div className="grid gap-2 sm:grid-cols-2">
          {[...causes].sort().map((cause) => {
            const disabled = cause === value.primary_cause || (!value.causes.includes(cause) && value.causes.length >= 7)
            return (
              <label key={cause} className={`flex items-center gap-2 text-sm ${disabled ? 'opacity-50' : ''}`}>
                <Checkbox
                  checked={value.causes.includes(cause)}
                  disabled={disabled}
                  onCheckedChange={(checked) => {
                    const next = checked
                      ? [...value.causes, cause]
                      : value.causes.filter((current) => current !== cause)
                    onChange({ ...value, causes: next })
                  }}
                />
                {cause}
              </label>
            )
          })}
        </div>
        {fieldError('causes') && <p className="text-base text-destructive">{fieldError('causes')}</p>}
      </div>
    </>
  )
}
